import React, { useEffect, useState, useRef, Component } from 'react';
import { Check, X, Star, ArrowRight, ShoppingBag, Clock, Heart, AlertCircle, MapPin } from 'lucide-react';
// --- Sales Notification Data ---
const recentSales = [{
  name: 'Valentina',
  city: 'Buenos Aires',
  time: 'hace 2 min'
}, {
  name: 'Camila',
  city: 'Córdoba',
  time: 'hace 5 min'
}, {
  name: 'Martina',
  city: 'Rosario',
  time: 'hace 8 min'
}, {
  name: 'Lucía',
  city: 'Mendoza',
  time: 'hace 12 min'
}, {
  name: 'Sofía',
  city: 'La Plata',
  time: 'hace 15 min'
}, {
  name: 'María',
  city: 'San Miguel de Tucumán',
  time: 'hace 18 min'
}, {
  name: 'Florencia',
  city: 'Mar del Plata',
  time: 'hace 22 min'
}, {
  name: 'Agustina',
  city: 'Salta',
  time: 'hace 25 min'
}, {
  name: 'Carolina',
  city: 'Santa Fe',
  time: 'hace 28 min'
}, {
  name: 'Julieta',
  city: 'San Juan',
  time: 'hace 32 min'
}, {
  name: 'Milagros',
  city: 'Neuquén',
  time: 'hace 35 min'
}, {
  name: 'Rocío',
  city: 'Bahía Blanca',
  time: 'hace 40 min'
}];
// --- Sales Notification Component ---
const SalesNotification = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSale, setCurrentSale] = useState(0);
  useEffect(() => {
    // Initial delay before first notification
    const initialDelay = setTimeout(() => {
      setIsVisible(true);
    }, 5000);
    // Cycle through notifications
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentSale(prev => (prev + 1) % recentSales.length);
        setIsVisible(true);
      }, 500); // Small delay for animation
    }, 8000); // Show every 8 seconds
    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, []);
  const sale = recentSales[currentSale];
  return <div className={`fixed bottom-20 md:bottom-6 left-4 z-40 transition-all duration-500 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}`} data-id="element-0">
      <div className="bg-white rounded-2xl shadow-2xl border border-stone-100 p-4 flex items-center gap-3 max-w-xs" data-id="element-1">
        <div className="w-12 h-12 bg-[#F5A88E]/20 rounded-full flex items-center justify-center text-2xl shrink-0" data-id="element-2">
          🛍️
        </div>
        <div className="min-w-0" data-id="element-3">
          <p className="text-sm font-medium text-stone-900 truncate" data-id="element-4">
            <span className="font-bold" data-id="element-5">{sale.name}</span> compró el kit
          </p>
          <p className="text-xs text-stone-500 flex items-center gap-1" data-id="element-6">
            <MapPin size={10} data-id="element-7" />
            {sale.city} · {sale.time}
          </p>
        </div>
        <button onClick={() => setIsVisible(false)} className="text-stone-400 hover:text-stone-600 p-1 shrink-0" data-id="element-8">
          <X size={14} data-id="element-9" />
        </button>
      </div>
    </div>;
};
// --- Components ---
const Button = ({
  children,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => <button className={`bg-[#F5A88E] hover:bg-[#E8957A] text-white font-medium py-4 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2 text-lg ${className}`} {...props} data-id="element-10">
    {children}
  </button>;
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-16 md:py-24 px-6 md:px-8 ${className}`} data-id="element-11">
    <div className="max-w-6xl mx-auto" data-id="element-12">{children}</div>
  </section>;
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => setIsVisible(entry.isIntersecting));
    });
    if (domRef.current) observer.observe(domRef.current);
    return () => {
      if (domRef.current) observer.unobserve(domRef.current);
    };
  }, []);
  return <div ref={domRef} className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
    transitionDelay: `${delay}ms`
  }} data-id="element-13">
      {children}
    </div>;
};
export function App() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const cartUrl = 'https://argrutinas.com/cart/42961983471825:1';
  const goToCart = () => {
    window.location.href = cartUrl;
  };
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="font-sans text-stone-800 bg-[#FFF8F3] min-h-screen selection:bg-[#F5A88E] selection:text-white overflow-x-hidden" data-id="element-14">
      {/* Navigation / Header */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-6'}`} data-id="element-15">
        <div className="max-w-6xl mx-auto px-6 flex justify-between items-center" data-id="element-16">
          <div className="font-serif text-2xl font-bold tracking-tight text-stone-900" data-id="element-17">
            Segunda Piel
          </div>
          <button onClick={goToCart} className="hidden md:block bg-stone-900 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-stone-800 transition-colors" data-id="element-18">
            Comprar Kit
          </button>
        </div>
      </nav>

      {/* 1. HERO SECTION */}
      <header className="relative pt-32 pb-16 md:pt-40 md:pb-32 overflow-hidden" data-id="element-19">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#FFF0E5] rounded-bl-[100px] -z-10 hidden md:block" data-id="element-20" />

        <div className="max-w-6xl mx-auto px-6 md:px-8" data-id="element-21">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-22">
            <div className="order-2 md:order-1" data-id="element-23">
              <FadeIn data-id="element-24">
                <div className="inline-block px-4 py-1.5 bg-[#F5A88E]/10 text-[#D67C5E] rounded-full text-sm font-bold mb-6 tracking-wide" data-id="element-25">
                  NUEVO MÉTODO
                </div>
                <h1 className="font-serif text-4xl md:text-6xl leading-tight font-bold text-stone-900 mb-6" data-id="element-26">
                  Tu base no es el problema.{' '}
                  <span className="text-[#D67C5E] italic" data-id="element-27">Tu mano sí.</span>
                </h1>
                <p className="text-xl md:text-2xl text-stone-600 mb-8 font-light leading-relaxed" data-id="element-28">
                  "Piel prolija" en 6 minutos (sin efecto máscara).
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-29">
                  <Button onClick={scrollToPricing} className="w-full sm:w-auto" data-id="element-30">
                    Quiero mi kit Segunda Piel
                  </Button>
                </div>
                <div className="mt-8 flex items-center gap-2 text-sm text-stone-500" data-id="element-31">
                  <div className="flex text-[#F5A88E]" data-id="element-32">
                    <Star size={16} fill="currentColor" data-id="element-33" />
                    <Star size={16} fill="currentColor" data-id="element-34" />
                    <Star size={16} fill="currentColor" data-id="element-35" />
                    <Star size={16} fill="currentColor" data-id="element-36" />
                    <Star size={16} fill="currentColor" data-id="element-37" />
                  </div>
                  <span data-id="element-38">Más de 1.200 argentinas ya lo usan</span>
                </div>
              </FadeIn>
            </div>

            <div className="order-1 md:order-2 relative" data-id="element-39">
              <FadeIn delay={200} data-id="element-40">
                <div className="relative rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/5] md:aspect-square" data-id="element-41">
                  <img src="https://cdn.magicpatterns.com/uploads/pcRuMRPd8NhAJ3d6EH9omq/2.png" alt="Mujer aplicando base con esponja" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" data-id="element-42" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/40 to-transparent h-1/3" data-id="element-43" />
                </div>
                {/* Floating Badge */}
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl hidden md:block max-w-xs animate-bounce-slow" data-id="element-44">
                  <p className="font-serif text-lg italic text-stone-800" data-id="element-45">
                    "Parece mi piel, pero mejor."
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </header>

      {/* 2. PROBLEM AGITATION */}
      <Section className="bg-white rounded-[3rem] my-8 mx-4 md:mx-8 shadow-sm" data-id="element-46">
        <div className="max-w-3xl mx-auto text-center" data-id="element-47">
          <FadeIn data-id="element-48">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-8 leading-snug" data-id="element-49">
              ¿Te pasó de maquillarte, sentirte divina… y dos horas después
              mirarte y pensar: <br data-id="element-50" />
              <span className="text-[#D67C5E] italic" data-id="element-51">
                "¿Por qué se me ve peor que sin maquillaje?"
              </span>
            </h2>

            <p className="text-lg text-stone-600 mb-12" data-id="element-52">
              No es drama. Es frustración real. Vos no querés un look teatral.
              Querés piel pareja, aspecto limpio y que no se note la base.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8 text-left" data-id="element-53">
            {[{
            title: 'Exceso de producto',
            desc: "Pusiste más cantidad 'para cubrir' y terminaste con una capa gruesa."
          }, {
            title: 'Arrastre vs Presión',
            desc: 'Arrastraste con brocha o mano en vez de presionar el producto.'
          }, {
            title: 'Piel no lista',
            desc: 'Te maquillaste arriba de una piel que todavía no estaba preparada.'
          }].map((item, i) => <FadeIn key={i} delay={i * 100} data-id="element-54">
                <div className="bg-[#FFF8F3] p-6 rounded-2xl h-full border border-[#F5A88E]/20" data-id="element-55">
                  <div className="w-10 h-10 bg-[#F5A88E]/20 rounded-full flex items-center justify-center text-[#D67C5E] font-bold mb-4" data-id="element-56">
                    {i + 1}
                  </div>
                  <h3 className="font-bold text-stone-900 mb-2" data-id="element-57">
                    {item.title}
                  </h3>
                  <p className="text-stone-600 text-sm" data-id="element-58">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* 3. MECHANISM REVEAL */}
      <Section data-id="element-59">
        <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-60">
          <FadeIn data-id="element-61">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-8 ring-white" data-id="element-62">
              <img src="https://cdn.magicpatterns.com/uploads/5S2u3oSFX5PHxJ1crNyN7n/heylloyd-image-1770166489685.png" alt="iPad mostrando el método Segunda Piel" className="w-full h-auto" data-id="element-63" />
            </div>
          </FadeIn>

          <FadeIn delay={200} data-id="element-64">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-stone-900 mb-6" data-id="element-65">
              Segunda Piel en <span className="text-[#D67C5E]" data-id="element-66">6 Minutos</span>
            </h2>
            <p className="text-xl text-stone-800 font-medium mb-6" data-id="element-67">
              El problema no es tu cara. El problema es que te enseñaron a usar
              la base como si fuera pintura.
            </p>
            <p className="text-stone-600 mb-6 leading-relaxed" data-id="element-68">
              Y la piel no es una pared. Se mueve. Transpira. Tiene textura.
              Cuando intentás tapar todo con más producto, pasa lo inevitable:
              se acumula en pliegues y marca lo que querías disimular.
            </p>
            <div className="bg-white p-6 rounded-2xl shadow-sm border-l-4 border-[#F5A88E]" data-id="element-69">
              <p className="font-medium text-stone-900" data-id="element-70">
                Este kit está armado alrededor de una idea simple:
                <br data-id="element-71" />
                <span className="text-[#D67C5E] text-lg font-bold block mt-2" data-id="element-72">
                  menos producto + mejor presión + orden correcto.
                </span>
              </p>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* 4. KIT CONTENTS */}
      <Section className="bg-white" data-id="element-73">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-74">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-75">
            ¿Qué incluye el Kit?
          </h2>
          <p className="text-stone-600" data-id="element-76">
            Todo lo que necesitás para el efecto "no makeup-makeup".
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-77">
          {[{
          name: 'Base Líquida',
          desc: 'Para emparejar, no para enmascarar. Fórmula liviana construible.',
          icon: '💧'
        }, {
          name: 'Esponja Pro',
          desc: 'Diseñada para difuminar con presión, no con arrastre.',
          icon: '🥚'
        }, {
          name: 'Espejo de Mano',
          desc: 'Para ver la verdad en el momento y corregir antes de salir.',
          icon: '🪞'
        }, {
          name: 'Guía Digital QR',
          desc: 'Acceso exclusivo al paso a paso y checklist diario.',
          icon: '📱'
        }].map((item, i) => <FadeIn key={i} delay={i * 100} data-id="element-78">
              <div className="bg-[#FFF8F3] p-8 rounded-3xl hover:shadow-lg transition-shadow duration-300 h-full flex flex-col items-center text-center group" data-id="element-79">
                <div className="text-4xl mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-80">
                  {item.icon}
                </div>
                <h3 className="font-bold text-lg text-stone-900 mb-3" data-id="element-81">
                  {item.name}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed" data-id="element-82">
                  {item.desc}
                </p>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* 5. BENEFITS */}
      <Section data-id="element-83">
        <div className="bg-[#F5A88E] rounded-[3rem] p-8 md:p-16 text-white overflow-hidden relative" data-id="element-84">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-16 -mt-16 blur-3xl" data-id="element-85" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/5 rounded-full -ml-16 -mb-16 blur-3xl" data-id="element-86" />

          <div className="grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-87">
            <div data-id="element-88">
              <h2 className="font-serif text-3xl md:text-5xl font-bold mb-8" data-id="element-89">
                Lo que cambia cuando lo hacés bien
              </h2>
              <ul className="space-y-6" data-id="element-90">
                {['Te arreglás rápido y salís segura.', 'Te sacás fotos sin miedo al zoom.', 'No sentís que tenés que estar retocándote.', 'No te peleás con el espejo del baño.'].map((benefit, i) => <li key={i} className="flex items-center gap-4 text-lg md:text-xl font-medium" data-id="element-91">
                    <div className="bg-white/20 p-2 rounded-full" data-id="element-92">
                      <Check size={20} className="text-white" data-id="element-93" />
                    </div>
                    {benefit}
                  </li>)}
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl border border-white/20" data-id="element-94">
              <div className="flex items-start gap-4 mb-6" data-id="element-95">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-[#F5A88E] font-bold text-xl" data-id="element-96">
                  S
                </div>
                <div data-id="element-97">
                  <p className="font-bold text-lg" data-id="element-98">Sofía M.</p>
                  <div className="flex text-yellow-300 text-sm" data-id="element-99">★★★★★</div>
                </div>
              </div>
              <p className="text-lg italic leading-relaxed opacity-90" data-id="element-100">
                "Siempre pensé que necesitaba bases de alta cobertura por mis
                manchitas. Con este método uso la mitad de producto y mi piel se
                ve increíble. Me cambió las mañanas."
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* 6. FOR WHO / NOT FOR WHO */}
      <Section className="bg-white" data-id="element-101">
        <div className="grid md:grid-cols-2 gap-8 md:gap-16" data-id="element-102">
          <FadeIn data-id="element-103">
            <div className="bg-[#F0FFF4] border border-green-100 p-8 rounded-3xl h-full" data-id="element-104">
              <h3 className="text-2xl font-bold text-green-800 mb-6 flex items-center gap-3" data-id="element-105">
                <Check className="bg-green-200 p-1 rounded-full text-green-700" size={32} data-id="element-106" />
                Para vos si...
              </h3>
              <ul className="space-y-4" data-id="element-107">
                {['Tenés 20+ y querés verte arreglada sin parecer producida.', 'Te molesta el efecto máscara y la pesadez.', 'Odiás cuando se marca la nariz o las líneas de expresión.', "Comprás por impulso cuando sentís 'esto me puede funcionar'."].map((item, i) => <li key={i} className="flex items-start gap-3 text-stone-700" data-id="element-108">
                    <Check size={18} className="text-green-600 mt-1 shrink-0" data-id="element-109" />
                    <span data-id="element-110">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={200} data-id="element-111">
            <div className="bg-[#FFF5F5] border border-red-100 p-8 rounded-3xl h-full" data-id="element-112">
              <h3 className="text-2xl font-bold text-red-800 mb-6 flex items-center gap-3" data-id="element-113">
                <X className="bg-red-200 p-1 rounded-full text-red-700" size={32} data-id="element-114" />
                No es para vos si...
              </h3>
              <ul className="space-y-4" data-id="element-115">
                {["Buscás un look 'full glam' pesado todos los días.", 'Querés tapar el 100% de la piel con una sola capa (eso siempre se nota).', 'Preferís el acabado mate extremo y seco.', 'No estás dispuesta a cambiar tu técnica de aplicación.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-stone-700" data-id="element-116">
                    <X size={18} className="text-red-500 mt-1 shrink-0" data-id="element-117" />
                    <span data-id="element-118">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* TESTIMONIALS SECTION */}
      <Section data-id="element-119">
        <div className="text-center mb-12" data-id="element-120">
          <FadeIn data-id="element-121">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-122">
              Lo que dicen nuestras clientas
            </h2>
            <p className="text-stone-600" data-id="element-123">
              Más de 1.200 argentinas ya transformaron su rutina
            </p>
          </FadeIn>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-124">
          {[{
          name: 'Valentina R.',
          city: 'Buenos Aires',
          rating: 5,
          text: 'Pensé que era otro producto más, pero la diferencia está en la técnica. La guía QR me cambió todo. Ahora tardo la mitad y me veo el doble de bien.',
          highlight: 'la técnica lo cambia todo'
        }, {
          name: 'Camila S.',
          city: 'Córdoba',
          rating: 5,
          text: 'Tengo 34 años y siempre se me marcaban las líneas de expresión con la base. Con el método de presión, desaparecieron. Mi marido me preguntó qué me hice.',
          highlight: 'desaparecieron las líneas'
        }, {
          name: 'Martina L.',
          city: 'Rosario',
          rating: 5,
          text: 'La regla del poroto me voló la cabeza. Usaba TRES veces más producto del necesario. Ahora me rinde muchísimo más y se ve natural.',
          highlight: 'usaba 3x más producto'
        }, {
          name: 'Lucía P.',
          city: 'Mendoza',
          rating: 5,
          text: 'Trabajo en oficina y siempre llegaba con la base corrida. Ahora aguanta todo el día sin retoques. El espejito es clave para el check final.',
          highlight: 'aguanta todo el día'
        }, {
          name: 'Agustina M.',
          city: 'La Plata',
          rating: 5,
          text: 'Soy de piel grasa y siempre terminaba brillosa. La técnica de las 3 zonas me solucionó la vida. Se ve mate pero no seco.',
          highlight: 'mate pero no seco'
        }, {
          name: 'Carolina F.',
          city: 'Mar del Plata',
          rating: 5,
          text: 'Le regalé el kit a mi mamá (tiene 58) y está fascinada. Dice que por fin encontró algo que no le marca los poros. Ahora quiere otro para mi hermana.',
          highlight: 'no marca los poros'
        }].map((testimonial, i) => <FadeIn key={i} delay={i * 100} data-id="element-125">
              <div className="bg-white p-6 rounded-2xl shadow-md border border-stone-100 h-full flex flex-col" data-id="element-126">
                <div className="flex items-center gap-3 mb-4" data-id="element-127">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#F5A88E] to-[#E8957A] rounded-full flex items-center justify-center text-white font-bold text-lg" data-id="element-128">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div data-id="element-129">
                    <p className="font-bold text-stone-900" data-id="element-130">
                      {testimonial.name}
                    </p>
                    <p className="text-xs text-stone-500 flex items-center gap-1" data-id="element-131">
                      <MapPin size={10} data-id="element-132" />
                      {testimonial.city}
                    </p>
                  </div>
                </div>

                <div className="flex text-[#F5A88E] mb-3" data-id="element-133">
                  {[...Array(testimonial.rating)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-134" />)}
                </div>

                <p className="text-stone-600 text-sm leading-relaxed flex-grow" data-id="element-135">
                  "{testimonial.text}"
                </p>

                <div className="mt-4 pt-4 border-t border-stone-100" data-id="element-136">
                  <span className="text-xs font-medium text-[#D67C5E] bg-[#F5A88E]/10 px-3 py-1 rounded-full" data-id="element-137">
                    ✨ {testimonial.highlight}
                  </span>
                </div>
              </div>
            </FadeIn>)}
        </div>

        <FadeIn delay={600} data-id="element-138">
          <div className="mt-12 text-center" data-id="element-139">
            <div className="inline-flex items-center gap-3 bg-[#FFF8F3] px-6 py-4 rounded-2xl border border-[#F5A88E]/20" data-id="element-140">
              <div className="flex -space-x-2" data-id="element-141">
                {['V', 'C', 'M', 'L', 'A'].map((initial, i) => <div key={i} className="w-8 h-8 bg-gradient-to-br from-[#F5A88E] to-[#E8957A] rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white" data-id="element-142">
                    {initial}
                  </div>)}
              </div>
              <div className="text-left" data-id="element-143">
                <p className="font-bold text-stone-900 text-sm" data-id="element-144">
                  +1.200 clientas satisfechas
                </p>
                <div className="flex items-center gap-1" data-id="element-145">
                  <div className="flex text-[#F5A88E]" data-id="element-146">
                    {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="currentColor" data-id="element-147" />)}
                  </div>
                  <span className="text-xs text-stone-500" data-id="element-148">4.9 promedio</span>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* 7. HOW TO USE */}
      <Section data-id="element-149">
        <div className="text-center mb-16" data-id="element-150">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900" data-id="element-151">
            Cómo se usa
          </h2>
          <p className="text-stone-600 mt-2" data-id="element-152">El ritual de 5 pasos</p>
        </div>

        <div className="relative" data-id="element-153">
          {/* Connecting line for desktop */}
          <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-[#F5A88E]/30 -z-10 transform -translate-y-1/2" data-id="element-154" />

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-155">
            {[{
            step: '1',
            title: 'Preparación',
            desc: 'Express según tu piel'
          }, {
            step: '2',
            title: 'Regla del Poroto',
            desc: 'Cantidad mínima'
          }, {
            step: '3',
            title: 'Esponja Húmeda',
            desc: '+ Presión (no arrastre)'
          }, {
            step: '4',
            title: '3 Zonas',
            desc: 'T / Mejillas / Perímetro'
          }, {
            step: '5',
            title: 'Checklist',
            desc: 'Antes de salir'
          }].map((item, i) => <FadeIn key={i} delay={i * 150} data-id="element-156">
                <div className="bg-white p-6 rounded-2xl shadow-md border border-stone-100 flex flex-col items-center text-center h-full relative z-10" data-id="element-157">
                  <div className="w-12 h-12 bg-[#F5A88E] text-white rounded-full flex items-center justify-center font-bold text-xl mb-4 shadow-lg ring-4 ring-[#FFF8F3]" data-id="element-158">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-stone-900 mb-1" data-id="element-159">
                    {item.title}
                  </h3>
                  <p className="text-sm text-stone-500" data-id="element-160">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* 8. PRICING + CTA */}
      <Section id="pricing" className="pb-32" data-id="element-161">
        <div className="max-w-4xl mx-auto" data-id="element-162">
          <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-[#F5A88E]/20" data-id="element-163">
            <div className="bg-[#F5A88E] py-4 text-center text-white font-medium tracking-wide text-sm uppercase" data-id="element-164">
              Oferta Especial Lanzamiento
            </div>

            <div className="p-8 md:p-16 text-center" data-id="element-165">
              <h2 className="font-serif text-3xl md:text-5xl font-bold text-stone-900 mb-4" data-id="element-166">
                Kit "Segunda Piel"
              </h2>
              <p className="text-stone-600 mb-8 text-lg" data-id="element-167">
                Base líquida + Esponja Pro + Espejo + Guía Digital QR
              </p>

              <div className="flex items-center justify-center gap-4 mb-10" data-id="element-168">
                <span className="text-5xl md:text-7xl font-bold text-stone-900 tracking-tight" data-id="element-169">
                  $39.700
                </span>
                <span className="text-xl text-stone-400 font-medium" data-id="element-170">ARS</span>
              </div>

              {/* Free Shipping Badge */}
              <div className="flex items-center justify-center gap-2 text-green-700 bg-green-50 border border-green-200 px-5 py-3 rounded-full text-base font-medium mx-auto mb-8" data-id="element-171">
                <Check size={18} className="text-green-600" data-id="element-172" />
                Envío GRATIS a domicilio · Llega en 4 a 8 días
              </div>

              <Button onClick={goToCart} className="w-full md:w-auto text-xl px-12 py-5 shadow-[#F5A88E]/40 hover:shadow-[#F5A88E]/60 mb-6" data-id="element-173">
                Quiero mi kit Segunda Piel <ArrowRight className="ml-2" data-id="element-174" />
              </Button>

              <div className="flex items-center justify-center gap-2 text-amber-600 bg-amber-50 inline-block px-4 py-2 rounded-lg text-sm font-medium mx-auto" data-id="element-175">
                <AlertCircle size={16} data-id="element-176" />
                Stock limitado: es producto físico en inventario
              </div>

              <div className="mt-8 pt-8 border-t border-stone-100 grid grid-cols-3 gap-4 text-xs md:text-sm text-stone-500" data-id="element-177">
                <div className="flex flex-col items-center gap-2" data-id="element-178">
                  <div className="bg-green-100 p-2 rounded-full" data-id="element-179">
                    <ShoppingBag size={16} className="text-green-600" data-id="element-180" />
                  </div>
                  <span className="text-center" data-id="element-181">
                    Envío Gratis
                    <br data-id="element-182" />a Domicilio
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2" data-id="element-183">
                  <div className="bg-stone-100 p-2 rounded-full" data-id="element-184">
                    <Clock size={16} data-id="element-185" />
                  </div>
                  <span className="text-center" data-id="element-186">
                    Llega en
                    <br data-id="element-187" />4 a 8 días
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2" data-id="element-188">
                  <div className="bg-stone-100 p-2 rounded-full" data-id="element-189">
                    <Heart size={16} data-id="element-190" />
                  </div>
                  <span data-id="element-191">Cruelty Free</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-400 py-12 px-6 text-center" data-id="element-192">
        <div className="max-w-6xl mx-auto" data-id="element-193">
          <div className="font-serif text-2xl font-bold text-white mb-6" data-id="element-194">
            Segunda Piel
          </div>
          <p className="text-sm mb-8" data-id="element-195">
            Tu belleza real, simplificada. Diseñado en Buenos Aires.
          </p>
          <div className="text-xs opacity-50" data-id="element-196">
            &copy; {new Date().getFullYear()} Segunda Piel. Todos los derechos
            reservados.
          </div>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className={`fixed bottom-0 left-0 w-full bg-white border-t border-stone-100 p-4 md:hidden z-50 transition-transform duration-300 ${scrolled ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-197">
        <div className="flex items-center justify-between gap-4" data-id="element-198">
          <div className="flex flex-col" data-id="element-199">
            <span className="text-xs text-stone-500 uppercase font-bold" data-id="element-200">
              Total
            </span>
            <span className="text-xl font-bold text-stone-900" data-id="element-201">$39.700</span>
          </div>
          <button onClick={goToCart} className="bg-[#F5A88E] text-white font-bold py-3 px-6 rounded-full flex-1 shadow-lg" data-id="element-202">
            Comprar Ahora
          </button>
        </div>
      </div>

      {/* Sales Notifications */}
      <SalesNotification data-id="element-203" />
    </div>;
}